/**
 * API Client for communicating with the backend
 *
 * Authentication strategy (per Shopify docs):
 * 1. Extract id_token from URL on initial load (Shopify passes this)
 * 2. Use it in Authorization header for API requests
 * 3. Fallback to shop domain header if no token available
 *
 * Note: We use XMLHttpRequest instead of fetch because App Bridge v4
 * intercepts fetch in ways that can cause hanging requests.
 */

/**
 * Get a fresh session token from App Bridge v4.
 * Falls back to id_token from URL if App Bridge isn't available.
 */
async function getSessionToken(): Promise<string | null> {
  // Prefer App Bridge (always returns a fresh, non-expired token)
  if (window.shopify?.idToken) {
    try {
      const token = await window.shopify.idToken()
      console.log('[API Client] Got fresh token from App Bridge')
      return token
    } catch (e) {
      console.warn('[API Client] App Bridge idToken() failed:', e)
    }
  }

  // Fallback: id_token from URL (may be expired)
  const urlParams = new URLSearchParams(window.location.search)
  const idToken = urlParams.get('id_token')
  if (idToken) {
    console.log('[API Client] Falling back to URL id_token')
    return idToken
  }

  console.warn('[API Client] No session token available')
  return null
}

/**
 * Get shop domain from App Bridge config (fallback auth)
 */
function getShopDomain(): string | null {
  return window.shopify?.config?.shop || null
}

// ============================================
// Core fetch wrapper using XMLHttpRequest
// (Avoids App Bridge fetch interception issues)
// ============================================

async function apiFetch<T>(
  endpoint: string,
  options: { method?: string; body?: string } = {}
): Promise<T> {
  const url = endpoint.startsWith('/') ? endpoint : `/${endpoint}`
  const method = options.method || 'GET'

  console.log(`[API Client] Making ${method} request to ${url}`)

  // Get fresh token before making request
  const token = await getSessionToken()

  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest()
    xhr.open(method, url, true)

    // Set headers
    xhr.setRequestHeader('Content-Type', 'application/json')

    // Add authentication
    if (token) {
      xhr.setRequestHeader('Authorization', `Bearer ${token}`)
      console.log('[API Client] Using fresh session token auth')
    } else {
      const shopDomain = getShopDomain()
      if (shopDomain) {
        xhr.setRequestHeader('X-Shopify-Shop-Domain', shopDomain)
        console.log('[API Client] Using shop domain fallback:', shopDomain)
      } else {
        console.warn('[API Client] No authentication available')
      }
    }

    xhr.onload = function() {
      console.log(`[API Client] Response status: ${xhr.status}`)

      if (xhr.status >= 200 && xhr.status < 300) {
        try {
          const data = JSON.parse(xhr.responseText)
          resolve(data)
        } catch (e) {
          reject(new Error('Invalid JSON response'))
        }
      } else {
        let errorMessage = `Request failed: ${xhr.status}`
        try {
          const errorData = JSON.parse(xhr.responseText)
          errorMessage = errorData.detail || errorMessage
        } catch (e) {
          // Use default error message
        }
        console.error('[API Client] Request failed:', xhr.status, xhr.responseText)
        reject(new Error(errorMessage))
      }
    }

    xhr.onerror = function() {
      console.error('[API Client] Network error')
      reject(new Error('Network error'))
    }

    xhr.ontimeout = function() {
      console.error('[API Client] Request timeout')
      reject(new Error('Request timeout'))
    }

    // Set timeout to 30 seconds
    xhr.timeout = 30000

    // Send request
    if (options.body) {
      xhr.send(options.body)
    } else {
      xhr.send()
    }
  })
}

// ============================================
// Config API
// ============================================

export interface BrandVoice {
  summary: string
  tone: string
  personality: string
  writingStyle: string
  messagingGuidelines: string
  keywordsToUse: string
  avoidPatterns: string
  cartRecoveryExamples: string
}

export interface ShopConfig {
  max_discount_percent: number
  offer_types: {
    percentage_off: boolean
    free_shipping: boolean
  }
  brand_voice: BrandVoice
  brand_voice_tone: string
  brand_voice_style: string
  example_message?: string
  shop_name?: string
}

export async function getConfig(): Promise<ShopConfig> {
  return apiFetch<ShopConfig>('/api/config')
}

export async function updateConfig(config: Partial<ShopConfig>): Promise<ShopConfig> {
  return apiFetch<ShopConfig>('/api/config', {
    method: 'PUT',
    body: JSON.stringify(config),
  })
}

// ============================================
// Message Preview API
// ============================================

export interface PreviewRequest {
  max_discount_percent: number
  offer_type: 'percentage_off' | 'free_shipping' | 'combined'
  cart_value?: number
  customer_name?: string
}

export interface PreviewResponse {
  message: string
  offer_description: string
  coupon_code: string
}

export async function generatePreview(request: PreviewRequest): Promise<PreviewResponse> {
  return apiFetch<PreviewResponse>('/api/config/preview-message', {
    method: 'POST',
    body: JSON.stringify(request),
  })
}

// ============================================
// Analytics API
// ============================================

export interface AnalyticsData {
  revenue_attributed: number
  revenue_change_percent: number
  discount_savings: number
  discount_savings_change_percent: number
  messages_sent: number
  messages_change_percent: number
  conversion_rate: number
  conversion_change_percent: number
  cost_per_message: number
  total_cost: number
  roi_multiplier: number
  daily_revenue: number[]
}

export async function getAnalytics(period: string = '30d'): Promise<AnalyticsData> {
  return apiFetch<AnalyticsData>(`/api/analytics?period=${period}`)
}

// ============================================
// Flow API
// ============================================

export interface FlowConfig {
  enabled: boolean
  trigger: 'abandoned_checkout'
  delay_minutes: number
  action: 'send_whatsapp'
}

export async function getFlowConfig(): Promise<FlowConfig> {
  return apiFetch<FlowConfig>('/api/flow')
}

export async function updateFlowConfig(config: Partial<FlowConfig>): Promise<FlowConfig> {
  return apiFetch<FlowConfig>('/api/flow', {
    method: 'PUT',
    body: JSON.stringify(config),
  })
}

// ============================================
// Messages/History API
// ============================================

export interface Message {
  id: string
  sent_at: string
  customer_phone: string
  customer_phone_masked: string
  offer_type: string
  offer_description: string
  cart_value: number
  status: 'sent' | 'delivered' | 'failed'
  message_content?: string
  coupon_code?: string
}

export interface MessagesResponse {
  messages: Message[]
  total: number
  page: number
  pages: number
}

export async function getMessages(page: number = 1, limit: number = 20): Promise<MessagesResponse> {
  return apiFetch<MessagesResponse>(`/api/messages?page=${page}&limit=${limit}`)
}

export async function getMessage(id: string): Promise<Message> {
  return apiFetch<Message>(`/api/messages/${id}`)
}

// Export
export const apiClient = {
  getConfig,
  updateConfig,
  generatePreview,
  getAnalytics,
  getFlowConfig,
  updateFlowConfig,
  getMessages,
  getMessage,
}

export { apiClient as api }
export default apiClient
